<template>
  <div class="mall-profit-chart">
    <div class="search-bar">
      <el-date-picker
        v-model="form.timeRange"
        v-bind="datePickerAttrs"
        size="medium"
        :clearable="false"
        :picker-options="pickerOptions"
        class="search-bar__date"
        range-separator="-"
        @change="getData"
      ></el-date-picker>

      <span class="search-bar__switch">
        <el-radio-group
          v-model="form.isMonth"
          size="small"
          @change="changeSwitch"
        >
          <el-radio-button :label="false">
            <div class="tooltip-wrapper">
              <span style="margin-right: 4px">日数据</span>
              <el-tooltip
                effect="dark"
                placement="top"
              >
                <div slot="content">
                  因会员收益按月统计及结算，按“日数据”方式查看不包含会员收益数据。
                </div>
                <i class="eqf-why-f"></i>
              </el-tooltip>
            </div>
          </el-radio-button>
          <el-radio-button :label="true">
            <div class="tooltip-wrapper">
              <span style="margin-right: 4px">月数据</span>
              <el-tooltip
                effect="dark"
                placement="top"
              >
                <div slot="content">
                  当月会员收益通常会在次月3号更新
                </div>
                <i class="eqf-why-f"></i>
              </el-tooltip>
            </div>
          </el-radio-button>
        </el-radio-group>
      </span>

      <span class="search-bar__select">
        按
        <el-select
          v-model="form.type"
          class="search-bar__select-inner"
          @change="getData"
        >
          <el-option
            v-for="(item, index) of filterTypeOptions"
            :key="index"
            :label="item.label"
            :value="item.value"
          >
          </el-option>
        </el-select>
        显示
      </span>
    </div>
    <div class="chart-wrapper">
      <div class="pie-chart-wrapper">
        <div
          ref="pie"
          class="pie-chart"
        ></div>
        <div class="pie-legend">
          <div
            v-if="pieData.length"
            class="pie-legend__title"
          >
            总收益 <span>{{ pieTotal }}</span>
          </div>
          <div
            :style="{ gridTemplateRows: pieData.length >= 4 ? `repeat(4, 17px)` : `repeat(${pieData.length}, 17px)` }"
            class="pie-legend__list"
          >
            <div
              v-for="(item, index) in pieData"
              :key="index"
              class="pie-legend__list-item"
            >
              <div
                :style="{ backgroundColor: getItemColor(item.name) }"
                class="item-color"
              ></div>
              <div class="item-name">
                {{ item.name }}
              </div>
              <div class="item-value">
                {{ item.value }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bar-chart-wrapper">
        <div
          ref="bar"
          class="bar-chart"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapState } from 'vuex'
import { getIncomeTrends } from 'api/showker'
import { getMonthBetween, getDateRangeArray } from 'src/utils/date'

export default {
  name: 'MallProfitChart',
  data() {
    return {
      // 搜索栏配置
      pickerOptions: {
        disabledDate(time) {
          const startTime = new Date('2019-01-01 00:00:00').getTime()
          return time.getTime() > Date.now() || time.getTime() < startTime
        }
      },
      form: {
        timeRange: [
          this.$moment().subtract(6, 'months').format('YYYY-MM'),
          this.$moment().format('YYYY-MM')
        ],
        isMonth: true,
        type: 0
      },
      typeOptions: [
        {
          label: '收益来源',
          value: 0
        },
        {
          label: '交易平台',
          value: 1
        },
        {
          label: '模板品类',
          value: 2
        }
      ],
      chartColor: ['#1261FF', '#FFA000', '#3AB37D', '#554BFB', '#FF5448', '#9EA4B5'],
      // 柱状图
      bar: null,
      // 饼图
      pie: null,
      pieTotal: 0,
      pieData: []
    }
  },
  computed: {
    ...mapState('user', ['userInfo']),
    datePickerAttrs() {
      return {
        type: this.form.isMonth ? 'monthrange' : 'daterange',
        valueFormat: this.form.isMonth ? 'yyyy-MM' : 'yyyy-MM-dd',
        key: this.form.isMonth ? 'MONTH' : 'DAY'
      }
    },
    filterTypeOptions() {
      let filterArray = this.form.isMonth ? [0] : [0, 1]

      return this.typeOptions.filter(item => filterArray.includes(item.value))
    }
  },
  mounted() {
    this.getData()

    window.addEventListener('resize', () => {
      if (this.bar) this.bar.resize()
      if (this.pie) this.pie.resize()
    })
  },
  beforeDestroy() {
    if (this.bar) this.bar.dispose()
    this.bar = null
    if (this.pie) this.pie.dispose()
    this.pie = null
  },
  methods: {
    async getData() {
      const { timeRange, isMonth, type } = this.form
      const params = {
        startDate: timeRange[0],
        endDate: timeRange[1],
        daily: !isMonth,
        queryType: type,
        showkerId: this.$store.state.user.userInfo.id
      }
      try {
        const { data } = await getIncomeTrends(params)
        if (data && data.obj) {
          this.initBar(data.obj.list)
          this.initPie(data.obj.total)
        }
      } catch (e) {
        throw e
      }
    },
    initBar(list) {
      if (this.bar) this.bar.dispose()
      const { timeRange, isMonth } = this.form
      const barData = {}
      const lineData = []
      const xLineData = []
      const xData = isMonth ? getMonthBetween(timeRange[0], timeRange[1]) : getDateRangeArray(timeRange[0], timeRange[1], 'day')
      xData.forEach((date, i) => {
        const index = list.findIndex(i => {
          return i.date === date
        })
        if (index > -1) {
          const item = list[index]
          let total = 0
          Object.entries(item.value).forEach(([name, value]) => {
            const val = name === '奖励收益' ? value : +(value / 100).toFixed(2) // value单位为分，这里转成元
            if (!barData[name]) {
              barData[name] = xData.map(() => 0)
            }
            barData[name][i] = val
            // total
            total += val
          })
          lineData.push(+total.toFixed(2))
        } else {
          lineData.push(0)
        }
        xLineData.push(0)
      })

      this.bar = echarts.init(this.$refs.bar)
      const option = {
        grid: {
          top: 20,
          right: 0,
          bottom: 0,
          left: 0,
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#fff',
          extraCssText: 'box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.1);border-radius: 4px;padding: 8px 12px;line-height: 24px;',
          textStyle: {
            color: '#333'
          },
          axisPointer: {
            lineStyle: {
              color: '#1261FF'
            },
            z: 2
          },
          formatter: function(params) {
            let result = ''
            let axisValue = ''
            params.forEach((item) => {
              axisValue = item.axisValue
              if (!['x轴', '总收益'].includes(item.seriesName)) {
                result += `
                  <span style="display:inline-block;margin-right:6px;border-radius:8px;width:8px;height:8px;background-color:${item.color}"></span>
                  <span>${item.seriesName}：</span>${item.data}</br>
                `
              }
            })
            return `
              ${axisValue}</br>
              ${result}
            `
          }
        },
        color: this.chartColor,
        xAxis: {
          type: 'category',
          data: xData,
          axisLine: {
            show: true,
            lineStyle: {
              color: '#ccc'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666'
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#ccc'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#666'
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#ccc'
            }
          }
        },
        series: [
          ...Object.entries(barData)
            .map(([name, value]) => ({
              type: 'bar',
              stack: '总收益',
              barMaxWidth: 30,
              name,
              data: value,
              itemStyle: {
                color: this.getItemColor(name)
              },
              z: 1,
              sortIndex: this.getSortIndex(name)
            }))
            .sort((a, b) => a.sortIndex - b.sortIndex),
          {
            type: 'line',
            name: '总收益',
            smooth: true,
            data: lineData,
            color: '#1261FF',
            lineStyle: {
              color: '#1261FF'
            },
            itemStyle: {
              opacity: 0
            },
            z: 1
          },
          {
            type: 'line',
            name: 'x轴',
            data: xLineData,
            color: '#1261FF',
            // tooltip: {
            //   show: false
            // },
            lineStyle: {
              color: '#1261FF',
              opacity: 0.5
            },
            z: 3
          }
        ]
      }
      this.bar.setOption(option, true)
    },
    initPie(data) {
      if (this.pie) this.pie.dispose()
      this.pie = echarts.init(this.$refs.pie)

      let total = 0
      const pieData = []

      Object.entries(data).forEach(([name, value]) => {
        const val = name === '奖励收益' ? value : +(value / 100).toFixed(2)
        total += val
        pieData.push({
          name,
          value: val,
          itemStyle: {
            color: this.getItemColor(name)
          },
          sortIndex: this.getSortIndex(name)
        })
      })

      this.pieTotal = +total.toFixed(2)
      this.pieData = pieData.map(v => v).sort((a, b) => a.sortIndex - b.sortIndex)

      const option = {
        title: {
          x: 'center',
          y: 'center',
          text: `{a|${this.pieTotal}}\n{b|总收益}`,
          textStyle: {
            rich: {
              a: {
                fontSize: 16,
                color: '#333',
                lineHeight: 18,
                fontWeight: 600
              },
              b: {
                fontSize: 12,
                color: '#333',
                lineHeight: 14,
                padding: [-8, 0, 0, 0]
              }
            }
          }
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: '#fff',
          extraCssText: 'box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.1);border-radius: 4px;padding: 8px 12px;line-height: 24px;',
          textStyle: {
            color: '#333'
          },

          formatter: function(param) {
            return `
               <span style="display:inline-block;margin-right:6px;border-radius:8px;width:8px;height:8px;background-color:${param.color}"></span>
               <span>${param.name}：</span>${param.value}
            `
          }
        },
        color: this.chartColor,
        series: [
          {
            type: 'pie',
            radius: ['55%', '75%'],
            data: pieData.sort((a, b) => b.value - a.value),
            clockwise: false,
            label: {
              show: false
            }
          }
        ]
      }

      this.pie.setOption(option, true)
    },
    changeSwitch(val) {
      // 重置参数
      this.form.type = 0

      if (val) {
        this.form.timeRange = [
          this.$moment().subtract(6, 'months').format('YYYY-MM'),
          this.$moment().format('YYYY-MM')
        ]
      } else {
        this.form.timeRange = [
          this.$moment().subtract(7, 'days').format('YYYY-MM-DD'),
          this.$moment().format('YYYY-MM-DD')
        ]
      }

      this.getData()
    },
    // 获取特定color
    getItemColor(name) {
      if (['订单收益', 'PC'].includes(name)) {
        return '#1261FF'
      }
      if (['分销收益', '小程序'].includes(name)) {
        return '#3AB37D'
      }
      if (['会员收益', '移动网页商城'].includes(name)) {
        return '#FFA000'
      }
      if (['APP'].includes(name)) {
        return '#554BFB'
      }
      if (['其他'].includes(name)) {
        return '#9EA4B5'
      }

      return '#1261FF'
    },
    // 获取排序
    getSortIndex(name) {
      let arr = []
      if (this.form.type === 0 && this.form.isMonth) {
        arr = ['订单收益', '会员收益', '分销收益']
      } else if (this.form.type === 0 && !this.form.isMonth) {
        arr = ['订单收益', '分销收益']
      } else if (this.form.type === 1 && !this.form.isMonth) {
        arr = ['PC', 'APP', '小程序', '移动网页商城']
      }

      const index = arr.findIndex(v => v === name)

      return index > -1 ? index : 99
    }
  }
}
</script>

<style lang="scss">
.mall-profit-chart {
  .el-radio-button {
    &.is-active {
      .eqf-why-f {
        color: #fff;
      }
    }
  }
}
</style>

<style lang="scss">
.mall-profit-chart .search-bar__date {
  .el-range-input {
    width: 42%;
  }

  .el-range__close-icon {
    display: none;
  }
}
</style>

<style lang="scss" scoped>
.mall-profit-chart {
    .search-bar {
        height: 36px;
        display: flex;
        align-items: center;
        margin-bottom: 20px;

        &__date {
            width: 230px;
            margin-right: 30px;
        }

        &__switch {
            display: inline-flex;
            align-items: center;
            margin-right: 30px;

            .tooltip-wrapper {
              display: inline-flex;
              align-items: center;
              line-height: 16px;
            }

            .eqf-why-f {
              font-size: 15px;
            }
        }

        &__select {
          &-inner {
            display: inline-block;
            width: 108px;
            margin: 0 4px;
          }
        }
    }
    .chart-wrapper {
      border-radius: 4px;
      border: 1px solid #E6EBED;
      padding: 25px;
    }
    .pie-chart-wrapper {
      position: relative;
      height: 200px;

      .pie-legend {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(0, -50%);

        &__title {
          font-weight: bold;
          color: #333333;
          line-height: 20px;
          font-size: 14px;
          margin-bottom: 12px;

          span {
            font-size: 16px;
            margin-left: 12px;
          }
        }

        &__list {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: repeat(4, 17px);
          grid-auto-flow: column;
          grid-gap: 8px 26px;

          &-item {
            display: flex;
            align-items: center;
            font-size: 12px;
            color: #545454;
            line-height: 17px;

            .item-color {
              width: 8px;
              height: 8px;
              border-radius: 50%;
            }

            .item-name,
            .item-value {
              margin-left: 12px;
            }
          }
        }
      }

      .pie-chart {
        width: 200px;
        height: 200px;
        position: absolute;
        right: 50%;
      }
    }
    .bar-chart {
      height: 260px;
    }
}
</style>
